import { Outlet, createFileRoute, redirect } from '@tanstack/react-router';

import { RouteError } from '@/components/composed/RouteError';
import { NotFound } from '@/components/composed/NotFound';
import { canAdministerAdminArea, redirectPathForRole } from '@/lib/permissions';
import { useAuthStore } from '@/lib/session/auth-store';

/*
 * Admin AREA layout route (spec §3.6; findings 1 + 2). It renders only an
 * <Outlet/> so its children — the admin landing (admin.index), user
 * administration (admin.users), permission administration (admin.permissions),
 * the account-population report — mount beneath it; the placeholder that used
 * to live here is now the landing at admin.index.tsx.
 *
 * beforeLoad reads the derived session from the store imperatively (never a
 * hook — guards run outside React) and gates the whole area on the
 * `is_administrator` PRIVILEGE via the one shared predicate the children and
 * the admin nav also read, so the parent, the children and the nav agree. A
 * privilege-but-non-admin-role account is therefore shown the nav AND let into
 * the area; an admin-role account WITHOUT the privilege is bounced home.
 */
export const Route = createFileRoute('/_auth/admin')({
  beforeLoad: () => {
    const session = useAuthStore.getState().session;
    if (!canAdministerAdminArea(session)) {
      throw redirect({
        to: redirectPathForRole(session?.role ?? 'client'),
      });
    }
  },
  component: AdminLayout,
  errorComponent: RouteError,
  notFoundComponent: NotFound,
});

function AdminLayout() {
  return <Outlet />;
}
